import { useMemo } from 'react';
import * as areaOperator from '@arcgis/core/geometry/operators/areaOperator';
import { planTypeKeysFor, useDelområderForArealplan } from '@origo/arealplan';
import type { Arealplan, ArealplanDelområde } from '@origo/arealplan';
import type { Område } from '@origo/seksjon';
import { groupFeatures } from '../../planer/utils/geometryUtils';
import {
    getFeatureHeading,
    getFeatureDescription,
    objectTypeAttributesMappings,
} from '../../planer/utils/planUtils';
import { vertikalnivåFor } from '../vertikalniva';

/**
 * Natural sort på norsk locale med numeric-collation. Gir ønsket rekkefølge
 * når strenger har leading SOSI-koder eller feltnavn med tall (B1 < B2 < B10).
 */
const compareNumeric = (a: string, b: string): number =>
    a.localeCompare(b, 'nb', { numeric: true, sensitivity: 'base' });

/**
 * Fjerner leading SOSI-prefiks fra en groupKey-streng slik at fallback-
 * visningen ikke får duplisert kode-prefiks i hver rad. Eksempler:
 *   "1111 - Boligbeb. - frittliggende småhus" → "Boligbeb. - frittliggende småhus"
 *   "110 - Boliger" → "Boliger"
 *   "2011 Kjøreveg" → "Kjøreveg"
 * Strenger uten leading tall returneres uendret.
 */
const stripSosiPrefix = (s: string): string =>
    s.replace(/^\s*\d+(?:\s*[-–:]\s*|\s+)/, '').trim();

export interface UseAdminAreasReturn {
    /**
     * To-nivås tre rotet ved delområdetype-grupper. Gruppe-noden har
     * `kind='delomrade'` men ingen geometri — `id` er gruppe-key (f.eks.
     * "3001 - Grønnstruktur") og `children` er konkrete FKB-delområder.
     * Gruppen har parentType=Plan; barna refererer gruppen via parentType=Delområdetype.
     */
    areas: Område[];
    planomrader: ArealplanDelområde[];
    /** Vertikalnivå-kode per delområde-id for delområder som ikke ligger på bakken. */
    vertikalnivåById: ReadonlyMap<string, string>;
    loading: boolean;
}

/**
 * Bygger admin-områdetreet for en plan ved å hente arealformål-features fra
 * `@origo/arealplan` og gruppere dem etter delområdetype-kode.
 *
 * Gruppene er virtuelle (ingen FeatureServer-rader) — de bygges på klienten
 * ut fra den matchende type-koden i hver feature.
 */
export function useAdminAreas(plan: Arealplan, outputWkid?: number): UseAdminAreasReturn {
    // Vi henter delområdene i kartets SR (outputWkid) slik at de kan rendres og
    // hit-testes direkte uten klient-side reprojisering av hundrevis av
    // polygoner. Til kartets SR er kjent holder vi queryen disabled ved å sende
    // udefinert planId — da unngår vi et bortkastet 25833-kall + refetch.
    // Ligger planen på flere vertikalnivåer, hentes delområdene fra alle.
    const ready = outputWkid != null;
    const { data: arealformål, isLoading } = useDelområderForArealplan(
        ready ? plan.planId : undefined,
        ready ? planTypeKeysFor(plan) : undefined,
        undefined,
        outputWkid
    );

    const areas = useMemo<Område[]>(() => {
        if (!arealformål || arealformål.length === 0) return [];
        const grouped = groupFeatures(arealformål, objectTypeAttributesMappings);

        const unsorted = Object.entries(grouped).map(([groupKey, groupFeaturesList]) => {
            const childRows = groupFeaturesList.map((feature) => {
                const id = feature.omradeId ?? String(feature.objectId ?? '');
                // FELTNAVN-display (f.eks. "B1", "BF") er det meningsfulle
                // individuelle navnet på et delområde. Når det er definert
                // bruker vi det alene — groupKey er allerede synlig som
                // forelder-noden, så det ville bare være duplisert støy.
                // Faller tilbake til formålets typeFelt-display strippet for
                // SOSI-koden ("110 - Boliger" → "Boliger") så fallback-rader
                // ikke får dobbel kode-prefiks.
                const feltnavn = getFeatureDescription(feature, objectTypeAttributesMappings)?.trim() ?? '';
                const formål = getFeatureHeading(feature, objectTypeAttributesMappings);
                const navn = feltnavn || stripSosiPrefix(formål) || id;
                const geometry = feature.geometri as __esri.Polygon | undefined;
                // Areal regnes klient-side fra geometrien (allerede hentet, WKID
                // 25833 → planar m²). Brukes som visning og som sorteringsnøkkel
                // for delområder uten feltnavn.
                let areaM2: number | undefined;
                try {
                    areaM2 = geometry
                        ? Math.abs(areaOperator.execute(geometry, { unit: 'square-meters' }))
                        : undefined;
                } catch {
                    areaM2 = undefined;
                }
                const område: Område = {
                    id,
                    kind: 'delomrade' as const,
                    navn,
                    planId: plan.planId,
                    parentType: 'Delområdetype' as const,
                    parentId: groupKey,
                    delområdetype: groupKey,
                    geometry,
                    areaM2,
                };
                return { område, feltnavn };
            });

            // Primær: feltnavn (naturlig tallsortering, B1 < B2 < B10) — navngitte
            // først. Delområder uten feltnavn sorteres etter, på areal synkende
            // (størst først) slik at meget små, irrelevante arealer havner nederst.
            childRows.sort((a, b) => {
                if (a.feltnavn && b.feltnavn) return compareNumeric(a.feltnavn, b.feltnavn);
                if (a.feltnavn) return -1;
                if (b.feltnavn) return 1;
                return (b.område.areaM2 ?? 0) - (a.område.areaM2 ?? 0);
            });

            const children: Område[] = childRows.map((r) => r.område);

            return {
                id: groupKey,
                kind: 'delomrade' as const,
                navn: groupKey,
                // `code` settes ikke for delområdetype-noder; groupKey er
                // allerede en human-readable streng (f.eks. «2011 - Kjøreveg»)
                // og TreeItem ville ellers vist samme tekst som både code-
                // prefiks og label.
                planId: plan.planId,
                parentType: 'Plan' as const,
                parentId: plan.planId,
                delområdetype: groupKey,
                children,
            };
        });

        // Sorter topp-nivå delområdetyper numerisk på groupKey/navn slik at
        // SOSI-prefix («1110 - Bolig», «1112 - Boligbeb …») gir 1110 < 1112,
        // ikke alfabetisk «310» < «3001».
        unsorted.sort((a, b) => compareNumeric(a.navn, b.navn));
        return unsorted;
    }, [arealformål, plan.planId]);

    const vertikalnivåById = useMemo<ReadonlyMap<string, string>>(() => {
        const map = new Map<string, string>();
        for (const f of arealformål ?? []) {
            const kode = vertikalnivåFor(f);
            if (kode) map.set(f.omradeId ?? String(f.objectId ?? ''), kode);
        }
        return map;
    }, [arealformål]);

    return {
        areas,
        planomrader: arealformål ?? [],
        vertikalnivåById,
        // Mens vi venter på kartets SR er queryen disabled; vis fortsatt
        // lasting så NavPanel-spinneren dekker hele ventetiden.
        loading: !ready || isLoading,
    };
}
